"use client";

import { useState, useMemo } from "react";
import Link from "next/link";
import { getAllSDKClasses, ROLE_CONFIG, LANG_CONFIG, type MethodRole } from "../../lib/sdk";
import Footer from "../../components/Footer";

const ALL_CLASSES = getAllSDKClasses();

function RoleChip({ role, count }: { role: MethodRole; count: number }) {
  if (count === 0) return null;
  const cfg = ROLE_CONFIG[role];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cfg.bg} ${cfg.text} border ${cfg.border}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {count} {cfg.label.toLowerCase()}
    </span>
  );
}

export default function FQNBrowserPage() {
  const [query, setQuery] = useState("");
  const [filterLang, setFilterLang] = useState<string>("all");
  const [filterRole, setFilterRole] = useState<string>("all");

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return ALL_CLASSES.filter((cls) => {
      if (filterLang !== "all" && cls.lang !== filterLang) return false;
      if (filterRole !== "all") {
        const hasRole = cls.methods.some((m) => m.role === filterRole);
        if (!hasRole) return false;
      }
      if (!q) return true;
      return (
        cls.name.toLowerCase().includes(q) ||
        cls.fqns.some((f) => f.toLowerCase().includes(q)) ||
        cls.patterns.some((p) => p.toLowerCase().includes(q)) ||
        cls.description.toLowerCase().includes(q)
      );
    });
  }, [query, filterLang, filterRole]);

  const langs = Array.from(new Set(ALL_CLASSES.map((c) => c.lang)));

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <div className="border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex items-center gap-1.5 text-sm font-mono text-gray-500 mb-5">
            <Link href="/sdk" className="hover:text-gray-900">sdk</Link>
            <span>/</span>
            <span className="text-gray-900 font-medium">fqn</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">FQN Browser</h1>
          <p className="text-base text-gray-600 max-w-2xl">
            Every fully-qualified name registered in the SDK. Search by class name, import path,
            or framework. Copy the FQN directly into your{" "}
            <code className="font-mono text-sm bg-gray-100 px-1.5 py-0.5 rounded text-gray-800">fqns = [...]</code>{" "}
            field.
          </p>

          {/* Search + filters */}
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <svg
                className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder='Search FQNs — try "gin", "gorm", "sqlite3"...'
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none text-sm text-gray-900 bg-white shadow-sm placeholder:text-gray-400"
                autoFocus
              />
            </div>

            {/* Lang filter */}
            <select
              value={filterLang}
              onChange={(e) => setFilterLang(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none shadow-sm"
            >
              <option value="all">All languages</option>
              {langs.map((l) => (
                <option key={l} value={l}>
                  {l.charAt(0).toUpperCase() + l.slice(1)}
                </option>
              ))}
            </select>

            {/* Role filter */}
            <select
              value={filterRole}
              onChange={(e) => setFilterRole(e.target.value)}
              className="px-3 py-2.5 rounded-xl border border-gray-300 text-sm text-gray-900 bg-white focus:border-green-500 focus:ring-2 focus:ring-green-200 outline-none shadow-sm"
            >
              <option value="all">All roles</option>
              <option value="source">Sources</option>
              <option value="sink">Sinks</option>
              <option value="sanitizer">Sanitizers</option>
            </select>
          </div>

          <p className="mt-3 text-xs text-gray-500">
            {filtered.length} of {ALL_CLASSES.length} classes
            {query && ` matching "${query}"`}
          </p>
        </div>
      </div>

      {/* Cards grid */}
      <div className="flex-1 max-w-6xl w-full mx-auto px-6 py-10">
        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-4xl mb-3">🔍</div>
            <p className="text-lg font-medium text-gray-700">No classes found</p>
            <p className="text-sm mt-1 text-gray-500">Try a different search term or clear the filters</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((cls) => {
              const langCfg = LANG_CONFIG[cls.lang] ?? { color: "text-gray-700", bg: "bg-gray-100", icon: cls.lang };
              const sources    = cls.methods.filter((m) => m.role === "source").length;
              const sinks      = cls.methods.filter((m) => m.role === "sink").length;
              const sanitizers = cls.methods.filter((m) => m.role === "sanitizer").length;

              return (
                <Link
                  key={`${cls.lang}-${cls.id}`}
                  href={`/sdk/${cls.lang}/${cls.category}/${cls.id}`}
                  className="group flex flex-col rounded-xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all bg-white overflow-hidden"
                >
                  {/* Card header */}
                  <div className="px-4 py-3.5 flex-1">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <code className="text-sm font-bold text-gray-900 font-mono group-hover:text-green-700 transition-colors truncate">
                        {cls.name}
                      </code>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold ${langCfg.bg} ${langCfg.color}`}>
                        {langCfg.icon}
                      </span>
                    </div>

                    {/* Primary FQN only */}
                    {cls.fqns[0] && (
                      <div className="text-xs font-mono text-gray-400 truncate mb-2" title={cls.fqns[0]}>
                        {cls.fqns[0]}
                      </div>
                    )}

                    <p className="text-xs text-gray-600 line-clamp-1">
                      {cls.description}
                    </p>
                  </div>

                  {/* Card footer */}
                  <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-center gap-2 flex-wrap">
                    <RoleChip role="source" count={sources} />
                    <RoleChip role="sink" count={sinks} />
                    <RoleChip role="sanitizer" count={sanitizers} />
                    <span className="ml-auto text-xs text-gray-500">
                      {cls.methods.length} methods
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 mb-1">Missing a framework?</h3>
            <p className="text-sm text-gray-600">
              Add a new QueryType class by inheriting from{" "}
              <code className="font-mono text-xs bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-800">QueryType</code>{" "}
              and setting <code className="font-mono text-xs bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-800">fqns</code>.
            </p>
          </div>
          <div className="flex gap-3">
            <Link
              href="/sdk/api"
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:border-gray-400 hover:bg-white transition-all"
            >
              API Reference
            </Link>
            <a
              href="https://github.com/shivasurya/code-pathfinder"
              target="_blank"
              rel="noreferrer"
              className="px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium hover:bg-gray-700 transition-colors"
            >
              Contribute →
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
